import { viewerApi } from './viewerApi'

// ---------------------------------------------------------------------------
// Dicionário de propriedades do modelo: que Property Sets e propriedades
// existem, e em quantos elementos. Amostra os itens com geometria (cap em
// PROPERTY_SAMPLE_LIMIT, como classColors/itemDimensions) e percorre
// IsDefinedBy -> IfcPropertySet(Name) -> HasProperties(Name), contando
// cada (pset, prop) UMA vez por elemento.
//
// O núcleo (aggregateDictionary) é puro e testável sobre os itens já
// lidos; buildPropertyDictionary é o adaptador que faz a amostragem.
// ---------------------------------------------------------------------------

const PROPERTY_SAMPLE_LIMIT = 10000

export interface DictionaryProp {
  name: string
  /** Nº de elementos (na amostra) que têm esta propriedade neste pset. */
  count: number
}
export interface DictionaryPset {
  name: string
  props: DictionaryProp[]
}
export interface PropertyDictionary {
  psets: DictionaryPset[]
  /** Nº de elementos amostrados. */
  sampled: number
  /** true quando o modelo tem mais itens do que o limite de amostragem. */
  truncated: boolean
}

/** Lê o atributo Name de uma entidade decodificada (aceita {value} ou
 *  valor cru). Devolve null quando ausente ou vazio. */
function attrName(o: Record<string, unknown>): string | null {
  const v = o.Name
  if (v == null) return null
  if (typeof v === 'object' && 'value' in (v as object)) {
    const raw = (v as { value: unknown }).value
    return raw == null || raw === '' ? null : String(raw)
  }
  return v === '' ? null : String(v)
}

/** Núcleo PURO: agrega os itens (com IsDefinedBy resolvido) num
 *  dicionário pset -> propriedades -> contagem. Pset ordenado por nome;
 *  propriedades por contagem desc e depois nome. */
export function aggregateDictionary(items: unknown[]): DictionaryPset[] {
  // pset name -> (prop name -> count)
  const psetMap = new Map<string, Map<string, number>>()
  for (const item of items) {
    if (!item || typeof item !== 'object') continue
    const rels = (item as Record<string, unknown>).IsDefinedBy as
      | unknown[]
      | undefined
    if (!Array.isArray(rels)) continue
    // Dentro de um elemento, contar cada (pset, prop) só uma vez.
    const seen = new Set<string>()
    for (const r of rels) {
      if (!r || typeof r !== 'object') continue
      const ro = r as Record<string, unknown>
      const psetName = attrName(ro)
      if (!psetName) continue
      const props = ro.HasProperties as unknown[] | undefined
      if (!Array.isArray(props)) continue
      for (const p of props) {
        if (!p || typeof p !== 'object') continue
        const propName = attrName(p as Record<string, unknown>)
        if (!propName) continue
        const k = `${psetName}\0${propName}`
        if (seen.has(k)) continue
        seen.add(k)
        let pm = psetMap.get(psetName)
        if (!pm) {
          pm = new Map()
          psetMap.set(psetName, pm)
        }
        pm.set(propName, (pm.get(propName) ?? 0) + 1)
      }
    }
  }
  return [...psetMap.entries()]
    .map(([name, pm]) => ({
      name,
      props: [...pm.entries()]
        .map(([n, count]) => ({ name: n, count }))
        .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name)),
    }))
    .sort((a, b) => a.name.localeCompare(b.name))
}

/** Constrói o dicionário de propriedades do modelo. Amostra os itens com
 *  geometria (on-demand; em modelos grandes custa segundos e memória) e
 *  agrega. Devolve null quando o modelo não está pronto ou a leitura
 *  falha. */
export async function buildPropertyDictionary(
  modelId: string,
): Promise<PropertyDictionary | null> {
  const model = viewerApi.getModel(modelId)
  if (!model) return null
  let allIds: number[]
  try {
    allIds = await model.getItemsIdsWithGeometry()
  } catch {
    return null
  }
  const truncated = allIds.length > PROPERTY_SAMPLE_LIMIT
  const ids = truncated ? allIds.slice(0, PROPERTY_SAMPLE_LIMIT) : allIds
  if (ids.length === 0) return { psets: [], sampled: 0, truncated }
  let items: unknown[]
  try {
    items = await model.getItemsData(ids, {
      attributesDefault: true,
      relationsDefault: { attributes: true, relations: true },
    })
  } catch {
    return null
  }
  return { psets: aggregateDictionary(items), sampled: ids.length, truncated }
}
